import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Alert,
  AutoComplete,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Typography
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import MoneyInput from "../../../serviceProvider/components/accounting/shared/MoneyInput";
import { formatCents } from "../../../serviceProvider/components/accounting/shared/money";
import * as accounting from "../../../serviceProvider/components/accounting/receivables/api";
import { writeOffReasonLabels } from "../../../utils/invoiceStatusArray";
import { invoiceErrorText } from "./invoiceErrors";
import { Success } from "../../Messages/Messages";
import "./InvoiceDialogs.css";

const iso = d => (d ? dayjs(d).format("YYYY-MM-DD") : undefined);
const beforeIssue = issueDate => current =>
  Boolean(issueDate && current && current.format("YYYY-MM-DD") < String(issueDate).slice(0, 10));

/**
 * Storno (ST-1, ST-2, ST-4): reason, date within an open period (not before the invoice date),
 * and "Storno und neu ausstellen", which opens the draft copy afterwards.
 */
export const StornoDialog = ({ open, invoice, onClose, onDone }) => {
  const { t: ta } = useTranslation("accounting");
  const history = useHistory();
  const [reason, setReason] = useState("");
  const [date, setDate] = useState(dayjs());
  const [reissue, setReissue] = useState(false);
  const [email, setEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setReason("");
    setDate(dayjs());
    setReissue(false);
    setEmail(false);
    setError("");
  }, [open]);

  const paidCash = Math.max(
    0,
    Number(invoice?.amounts?.paid || 0) -
      Number(invoice?.amounts?.credited || 0) -
      Number(invoice?.amounts?.writtenOff || 0)
  );
  const isCorrection = invoice?.type === "credit_note";

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await accounting.cancelInvoice(invoice._id, {
        reason,
        date: iso(date),
        reissue: reissue && !isCorrection,
        email
      });
      Success(`${ta("Cancellation invoice created")}: ${res.data.invoice?.invoiceNumber || ""}`);
      if (res.data.credit?.amount)
        Success(
          ta("A customer credit of {{amount}} was created", {
            amount: formatCents(res.data.credit.amount)
          })
        );
      onClose();
      if (res.data.replacement?._id) {
        Success(ta("The new draft invoice is open. Correct it and send it."));
        history.push(`/invoices/details/${res.data.replacement._id}`);
      } else onDone?.(res.data);
    } catch (err) {
      setError(invoiceErrorText(ta, err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="invoice-dialog"
      title={ta("Cancel invoice (Storno)")}
      open={open}
      width={600}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          {ta("Cancel")}
        </Button>,
        <Button key="ok" type="primary" danger loading={busy} onClick={submit}>
          {reissue && !isCorrection ? ta("Cancel and reissue") : ta("Create cancellation invoice")}
        </Button>
      ]}
    >
      <Form layout="vertical" className="invoice-dialog__body">
        {error ? <Alert type="error" showIcon message={error} /> : null}
        <Typography.Paragraph type="secondary">
          {ta(
            "A cancellation invoice with mirrored lines is created, the ledger entry is reversed and the original invoice is marked as cancelled. This cannot be undone."
          )}
        </Typography.Paragraph>
        {paidCash > 0 ? (
          <Alert
            type="warning"
            showIcon
            message={ta("The amount already paid ({{amount}}) becomes a customer credit.", {
              amount: formatCents(paidCash)
            })}
          />
        ) : null}
        <Form.Item
          label={ta("Cancellation date")}
          extra={ta("Not before the invoice date and not in a closed period")}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD.MM.YYYY"
            allowClear={false}
            value={date}
            disabledDate={beforeIssue(invoice?.issueDate)}
            onChange={setDate}
          />
        </Form.Item>
        <Form.Item label={ta("Reason (optional)")}>
          <Input.TextArea
            rows={3}
            maxLength={500}
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </Form.Item>
        {!isCorrection ? (
          <Checkbox checked={reissue} onChange={e => setReissue(e.target.checked)}>
            {ta("Cancel and reissue (creates a corrected draft copy)")}
          </Checkbox>
        ) : null}
        {reissue && paidCash > 0 ? (
          <Typography.Text type="secondary" className="invoice-dialog__hint">
            {ta("The payment moves to the new invoice when you send it.")}
          </Typography.Text>
        ) : null}
        <Checkbox checked={email} onChange={e => setEmail(e.target.checked)}>
          {ta("Email the cancellation invoice to the customer")}
        </Checkbox>
      </Form>
    </Modal>
  );
};

const FIELD_SUGGESTIONS = [
  "Anschrift des Leistungsempfängers",
  "Name des Leistungsempfängers",
  "USt-IdNr. des Leistungsempfängers",
  "Leistungszeitraum",
  "Leistungsdatum",
  "Steuernummer / USt-IdNr. des Leistenden"
];

/** Rechnungsberichtigung (ST-3): formal fields only (label, before, after), no amounts. */
export const CorrectionNoticeDialog = ({ open, invoice, onClose }) => {
  const { t: ta } = useTranslation("accounting");
  const history = useHistory();
  const [rows, setRows] = useState([{ label: "", before: "", after: "" }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows([{ label: "", before: "", after: "" }]);
    setError("");
    setTouched(false);
  }, [open]);

  const setRow = (i, key, value) =>
    setRows(prev => prev.map((r, j) => (j === i ? { ...r, [key]: value } : r)));
  const valid = rows.filter(r => r.label.trim() && r.after.trim());

  const submit = async send => {
    setTouched(true);
    if (!valid.length) {
      setError(ta("Name at least one corrected field."));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await accounting.createCorrectionNotice(invoice._id, { fields: valid });
      Success(ta("Correction notice saved as draft"));
      onClose();
      history.push(`/invoices/details/${res.data.invoice._id}`, send ? { openSend: true } : {});
    } catch (err) {
      setError(invoiceErrorText(ta, err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="invoice-dialog"
      title={`${ta("Correction notice")} – ${invoice?.invoiceNumber || ""}`}
      open={open}
      width={760}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          {ta("Cancel")}
        </Button>,
        <Button key="draft" loading={busy} onClick={() => submit(false)}>
          {ta("Save draft")}
        </Button>,
        <Button key="send" type="primary" loading={busy} onClick={() => submit(true)}>
          {ta("Save and send")}
        </Button>
      ]}
    >
      <div className="invoice-dialog__body">
        {error ? <Alert type="error" showIcon message={error} /> : null}
        <Typography.Paragraph type="secondary">
          {ta(
            "Use this when only a formal detail was wrong or missing (for example the address, a VAT ID or the service period). Amounts cannot change; for that use an invoice correction or a cancellation."
          )}
        </Typography.Paragraph>
        {rows.map((r, i) => (
          <div key={i} className="invoice-dialog__notice-row">
            <label className="invoice-dialog__field">
              <span>{ta("Field")}</span>
              <AutoComplete
                value={r.label}
                options={FIELD_SUGGESTIONS.map(value => ({ value }))}
                onChange={value => setRow(i, "label", value)}
                filterOption={(input, option) =>
                  option.value.toLowerCase().includes(String(input).toLowerCase())
                }
                status={touched && !r.label.trim() ? "error" : undefined}
              />
            </label>
            <label className="invoice-dialog__field">
              <span>{ta("Previously")}</span>
              <Input value={r.before} onChange={e => setRow(i, "before", e.target.value)} />
            </label>
            <label className="invoice-dialog__field">
              <span>{ta("Corrected")}</span>
              <Input
                value={r.after}
                status={touched && !r.after.trim() ? "error" : undefined}
                onChange={e => setRow(i, "after", e.target.value)}
              />
            </label>
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              aria-label={ta("Remove line")}
              disabled={rows.length === 1}
              className="invoice-dialog__free-remove"
              onClick={() => setRows(prev => prev.filter((_, j) => j !== i))}
            />
          </div>
        ))}
        <Button
          icon={<PlusOutlined />}
          onClick={() => setRows(prev => [...prev, { label: "", before: "", after: "" }])}
        >
          {ta("Add field")}
        </Button>
      </div>
    </Modal>
  );
};

/** Forderungsausfall (FA-1): write off the open amount or part of it (Vergleich). */
export const WriteOffDialog = ({ open, invoice, onClose, onDone }) => {
  const { t: ta } = useTranslation("accounting");
  const openAmount = Math.max(0, Number(invoice?.amounts?.open || 0));
  const [amount, setAmount] = useState(openAmount);
  const [reason, setReason] = useState("unreachable");
  const [note, setNote] = useState("");
  const [date, setDate] = useState(dayjs());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setAmount(openAmount);
    setReason("unreachable");
    setNote("");
    setDate(dayjs());
    setError("");
  }, [open]);

  const submit = async () => {
    if (!amount || amount <= 0 || amount > openAmount) {
      setError(ta("The amount must be greater than zero and at most the open amount."));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const res = await accounting.writeOffInvoice(invoice._id, {
        amount,
        reason,
        note,
        date: iso(date)
      });
      Success(ta("Receivable written off"));
      onDone?.(res.data);
    } catch (err) {
      setError(invoiceErrorText(ta, err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="invoice-dialog"
      title={ta("Write off receivable")}
      open={open}
      width={560}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          {ta("Cancel")}
        </Button>,
        <Button key="ok" type="primary" danger loading={busy} onClick={submit}>
          {ta("Write off")}
        </Button>
      ]}
    >
      <Form layout="vertical" className="invoice-dialog__body">
        {error ? <Alert type="error" showIcon message={error} /> : null}
        <Typography.Paragraph type="secondary">
          {ta(
            "The VAT on the written-off amount is corrected in the period of the write-off (§ 17 UStG). The invoice leaves dunning and the open items. A later payment revives it."
          )}
        </Typography.Paragraph>
        <Form.Item
          label={ta("Amount to write off")}
          extra={`${ta("Open amount")}: ${formatCents(openAmount)}`}
        >
          <MoneyInput min={0} value={amount} onChange={setAmount} />
        </Form.Item>
        {amount > 0 && amount < openAmount ? (
          <Alert
            type="info"
            showIcon
            message={ta("Partial write-off (settlement): {{amount}} stays open", {
              amount: formatCents(openAmount - amount)
            })}
          />
        ) : null}
        <Form.Item label={ta("Reason")}>
          <Select
            value={reason}
            onChange={setReason}
            options={Object.entries(writeOffReasonLabels).map(([value, label]) => ({
              value,
              label: ta(label)
            }))}
          />
        </Form.Item>
        <Form.Item label={ta("Date")}>
          <DatePicker
            style={{ width: "100%" }}
            format="DD.MM.YYYY"
            allowClear={false}
            value={date}
            disabledDate={beforeIssue(invoice?.issueDate)}
            onChange={setDate}
          />
        </Form.Item>
        <Form.Item label={ta("Note")}>
          <Input.TextArea
            rows={2}
            maxLength={500}
            value={note}
            onChange={e => setNote(e.target.value)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
